import { useContext, useEffect, useState } from 'react';

import {
	BookOutlined,
	DeleteOutlined,
	EditOutlined,
	ReloadOutlined,
	SearchOutlined,
	UnorderedListOutlined,
} from '@ant-design/icons';
import {
	Button,
	Checkbox,
	Flex,
	Input,
	Popconfirm,
	Space,
	Spin,
	Table,
	Tooltip,
	message,
} from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { SubscriptionContext } from '@/App';
import {
	useDeleteSingleMonitor,
	useGetContentMonitors,
} from '@/api/monitor_collections';
import { SUBSCRIPTIONS_TYPES_ENUM } from '@/const';
import { useDebounce } from '@/hooks';
import hasSubscriptionType from '@/lib/helpers/hasSubscriptionType';
import { PageContext } from '@/providers/PageContextProvider';

import { IPagination } from '../video-intelligence-on-demand';
import { columns } from './data';

export default function ContentMonitor() {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();

	const { subscriptions, subscriptionLoading } =
		useContext(SubscriptionContext);
	const { setPageTitle, openDrawer, setIsCopilotCreditsCounterVisible } =
		useContext(PageContext);
	const [currentItem, setCurrentItem] = useState<number | null>(null);
	const [pagination, setPagination] = useState<IPagination>({
		page: Number(searchParams.get('page') ?? 1),
		pageCount: 1,
		total: 0,
		limit: Number(searchParams.get('limit') ?? 10),
	});
	// const [filterUserInsights, setFilterUserInsights] = useState(
	// 	localStorage?.getItem('sv_show_user_insights') === '1' ? true : false,
	// );

	const [search, setSearch] = useState(searchParams.get('search') ?? '');
	const [sortBy, setSortBy] = useState<'title' | 'created_at'>('title');
	const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('asc');

	const debouncedSearch = useDebounce(search);

	const { data, isLoading, isFetching, refetch } = useGetContentMonitors({
		page: pagination.page,
		limit: pagination.limit,
		sortBy,
		sortOrder,
		search: debouncedSearch.length >= 3 ? debouncedSearch : '',
	});

	const { mutate: deleteMonitor, isLoading: isDeleteMonitorLoading } =
		useDeleteSingleMonitor();

	const paginationData = data?.data?.data?.pagination ?? {};

	const items = data?.data?.data?.items ?? [];

	useEffect(() => {
		setPageTitle('Brand Alert');
		setIsCopilotCreditsCounterVisible(true);

		return () => {
			setPageTitle('');
			setIsCopilotCreditsCounterVisible(false);
		};
	}, []);

	useEffect(() => {
		const pageParam = searchParams.get('page');
		const limitParam = searchParams.get('limit');
		const searchParam = searchParams.get('search');

		setSearchParams({
			page: pageParam ?? pagination?.page?.toString(),
			limit: limitParam ?? pagination?.limit?.toString(),
			search: searchParam ?? '',
		});
	}, []);

	useEffect(() => {
		refetch();
	}, [sortOrder, pagination]);

	useEffect(() => {
		if (!subscriptionLoading && subscriptions && subscriptions?.length > 0) {
			if (
				!hasSubscriptionType(subscriptions, SUBSCRIPTIONS_TYPES_ENUM.copilot)
			) {
				navigate('/app/dashboard/product-main-menu');
			}
		} else if (
			!subscriptionLoading &&
			subscriptions &&
			subscriptions?.length === 0
		) {
			navigate('/app/dashboard/product-main-menu');
		}
	}, [subscriptions, subscriptionLoading]);

	const onTableChange = (__: any, _: any, sorter: any) => {
		const { field = 'created_at', order = 'ascend' } = sorter;

		if (field) {
			setSortBy(field);
			// setSearchParams((searchParams) => {
			// 	searchParams.set('sortBy', field);
			// 	return searchParams;
			// });
		}

		if (order) {
			setSortOrder(order === 'ascend' ? 'asc' : 'desc');
			// setSearchParams((searchParams) => {
			// 	searchParams.set('sortOrder', order === 'ascend' ? 'asc' : 'desc');
			// 	return searchParams;
			// });
		}
	};

	const onNewMonitorClick = () => {
		// setDrawer
		openDrawer('add-new-monitor');
	};

	const onDeleteClick = (id: number) => {
		deleteMonitor(id, {
			onSuccess: () => {
				message.success('Removed');
				// setSearch("");
				setCurrentItem(null);
			},
			onError: (error) => {
				const err = error as any;
				console.error('Error deleting collection:', err.response.data.message);
				message.error(err.response.data.message);
				setCurrentItem(null);
			},
		});
	};

	const onEditClick = (id: number) => {
		openDrawer('edit-monitor', {
			id,
		});
	};

	return (
		<Spin spinning={isLoading || isFetching}>
			<div>
				<Flex
					align="center"
					justify="space-between"
					style={{ marginBottom: '1.5rem' }}
				>
					<h2>Brand Alert</h2>

					<Space>
						<Tooltip title="Refresh" placement="left">
							<Button onClick={() => refetch()} icon={<ReloadOutlined />} />
						</Tooltip>
					</Space>
				</Flex>

				<Flex vertical gap={16} style={{ width: '100%' }}>
					<Flex
						gap={8}
						justify="space-between"
						align="center"
						style={{ width: '100%' }}
					>
						<Input
							placeholder="Search..."
							suffix={<SearchOutlined />}
							style={{ width: '300px', maxWidth: '300px' }}
							value={search}
							onChange={(e) => {
								const value = e.target.value;
								setSearchParams((searchParams) => {
									searchParams.set('search', value ? value.toString() : '');
									return searchParams;
								});
								setSearch(value);
							}}
						/>
						<Button type="primary" onClick={onNewMonitorClick}>
							New monitor collection
						</Button>
					</Flex>

					<Table
						loading={isLoading || isFetching}
						// style={{ width: '100%' }}
						onChange={onTableChange}
						dataSource={items}
						columns={[
							...columns,
							{
								title: 'Actions',
								dataIndex: 'actions',
								key: 'actions',
								width: 120,
								ellipsis: false,
								fixed: 'right',
								// flex: true,
								render: (_: any, record: any) => {
									return (
										<Flex justify="center" align="center" gap={8}>
											<Tooltip title="View">
												<Button
													size="small"
													onClick={() =>
														navigate(
															`/app/dashboard/content-monitor/${record?.id}`,
														)
													}
												>
													<UnorderedListOutlined />
												</Button>
											</Tooltip>
											<Tooltip title="Edit">
												<Button
													size="small"
													onClick={() => onEditClick(Number(record?.id))}
												>
													<EditOutlined />
												</Button>
											</Tooltip>
											<Tooltip title="Remove">
												<Popconfirm
													title={`Sure to remove monitor?`}
													onConfirm={() => {
														setCurrentItem(Number(record?.id));
														onDeleteClick(Number(record?.id));
													}}
													placement="topLeft"
												>
													<Button
														danger
														size="small"
														loading={
															isDeleteMonitorLoading &&
															currentItem === record?.id
														}
														disabled={isDeleteMonitorLoading}
													>
														<DeleteOutlined />
													</Button>
												</Popconfirm>
											</Tooltip>
										</Flex>
									);
								},
							},
						]}
						rowKey={'id'}
						bordered
						scroll={{ x: 1100 }}
						pagination={{
							total: paginationData?.total,
							showSizeChanger: true,
							// defaultCurrent: paginationData?.page,
							current: paginationData?.page,
							pageSize: paginationData?.limit,
							pageSizeOptions: [10, 25, 50],
							onChange: (page: number, pageSize: number) => {
								setPagination((cur) => ({ ...cur, page, limit: pageSize }));
								setSearchParams((searchParams) => {
									searchParams.set('page', page.toString());
									searchParams.set('limit', pageSize.toString());
									return searchParams;
								});
							},
						}}
					/>
				</Flex>
			</div>
		</Spin>
	);
}
